import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo } from 'react';

import { trackGAEventWithCommunityAndUser } from '@/utility/analytics';
import config from '@/utility/config';
import { STATIC_ASSET_BASE_URL } from '@/utility/constants';
import { t } from '@/utility/localization';
import {
  CM_PORTAL_ADD_NEW_EVENT_ROUTE,
  CM_PORTAL_LIBRARY_PAGE_ROUTE,
  CM_PORTAL_PUBLIC_PAGE_ROUTE // CM_PORTAL_SETTINGS_PAGE_ROUTE
} from '@/utility/routesHelper';
import sessionStorageService from '@/utility/sessionStorageService';

import { setMoneyPageViewedService } from '@/services/moneyPageService';

import { useAuthContext } from '@/contexts/AuthContext';
import { useCommunityPortalContext } from '@/contexts/CommunityPortalContext';
import { useMoneyPageContext } from '@/contexts/MoneyPageContext';
import { useWindowWidthContext } from '@/contexts/WindowWidthContext';

import StatusTabs from '../components/StatusTabs';
import { EVENT_ACCESS_PAID } from '../events/constants';
import { TYPES_OF_PAYMENT_METHODS } from '../settings/Components/PriceSetting/constants';
import { MEMBER_ACCESS_TAB_ID } from '../settings/constants';
import IconTitleDescCtaCard from './components/IconTitleDescCtaCard';
import getConstants, {
  EARNINGS_TAB,
  FEES_TAB,
  PAYOUT_TAB,
  TRANSACTIONS_TAB
} from './consts';
import {
  getAccessibleMoneyTabs,
  getCommunityPaymentType,
  trackClickedCreatePaidEvent
} from './utils';

const PayoutTabV2 = dynamic(() => import('./tabs/PayoutTabV2'), {
  ssr: false
});
const EarningsTab = dynamic(() => import('./tabs/EarningsTab'), {
  ssr: false
});
const TransactionsTab = dynamic(() => import('./tabs/TransactionsTab'), {
  ssr: false
});
const PayoutTab = dynamic(() => import('./tabs/PayoutTab'), {
  ssr: false
});
const FeesTab = dynamic(() => import('./tabs/FeesTab'), {
  ssr: false
});

const setupPaidMembershipCardStaticProps = () => ({
  icon: (
    <img
      width={64}
      height={64}
      src={`${STATIC_ASSET_BASE_URL}/nasIO/community-events/paid-membership-icon.png`}
      alt="paid membership"
    />
  ),
  title: t('paid-memberships'),
  desc: t('generate-recurring-revenue-with-a-subscription-fee'),
  ctaTitle: t('setup-membership')
});

const setupPaidEventCardStaticProps = () => ({
  icon: (
    <img
      width={64}
      height={64}
      src={`${STATIC_ASSET_BASE_URL}/nasIO/community-events/paid-event-icon.png`}
      alt="Event paid content"
    />
  ),
  title: t('sell-paid-events'),
  desc: t('monetize-your-guest-speaker-sessions-or-workshops'),
  ctaTitle: t('create-paid-event')
});

const setupPaidLibraryCardStaticProps = () => ({
  icon: (
    <img
      width={64}
      height={64}
      src={`${STATIC_ASSET_BASE_URL}/nasIO/portal/png/NPL-Image-fixed-aspect-ratio-spacer.png`}
      alt="Library content"
    />
  ),
  title: t('sell-paid-product'),
  desc: t(
    'monetize-by-offering-digital-products-courses-and-1-1-sessions'
  ),
  ctaTitle: t('create-paid-product')
});

export const COMM_MONEY_PAGE_VIEW_MAP_SESSION_KEY =
  'communityToHasViewedMoneyPageMap';

const className = 'c-MoneyPageContent';

const MoneyPageContent = () => {
  const { revenueTypes } = getConstants();
  const { localEditActiveCommunity } = useCommunityPortalContext();
  const router = useRouter();

  const { user } = useAuthContext();

  const {
    activeTabId,
    onStatusTabClick,
    onChangeMonthFilter,
    earningsData,
    revenueTypesSelected,
    setRevenueTypesSelected,
    isLoading,
    chartData,
    membershipCounts,
    activeCommunity,
    payoutInfo,
    handleCloseMonthFilterTooltip,
    monthFilterTooltip
  } = useMoneyPageContext();

  const { isGtEqMd } = useWindowWidthContext();
  const communityPaymentType = getCommunityPaymentType(activeCommunity);
  const isPaidCommunity =
    communityPaymentType === TYPES_OF_PAYMENT_METHODS.paid;

  const hasPaidSubscriptions = useMemo(() => {
    if (!membershipCounts) return false;
    return membershipCounts?.total > 0 || isPaidCommunity;
  }, [membershipCounts, isPaidCommunity]);
  const hasPaidProducts =
    activeCommunity?.hasPaidEvents || activeCommunity?.hasFolders;

  const goToCreatePaidEventPage = () => {
    trackClickedCreatePaidEvent(user, activeCommunity);
    router.push({
      pathname: CM_PORTAL_ADD_NEW_EVENT_ROUTE,
      query: {
        access: EVENT_ACCESS_PAID
      }
    });
  };

  const goToCreatePaidMembershipPage = () => {
    trackGAEventWithCommunityAndUser(
      'money_page_create_paid_membership_clicked'
    );
    router.push({
      pathname: `${CM_PORTAL_PUBLIC_PAGE_ROUTE}`,
      query: { tab: MEMBER_ACCESS_TAB_ID, addPaid: true }
    });
  };

  const goToCreatePaidLibraryContentPage = () => {
    trackGAEventWithCommunityAndUser(
      'money_page_create_paid_content_clicked'
    );
    router.push({
      pathname: CM_PORTAL_LIBRARY_PAGE_ROUTE,
      query: { addPaid: true }
    });
  };

  const renderContentCards = () => {
    return (
      <>
        {/* Setup Paid Events Card */}
        <div className="w-full h-full mt-12 rounded-12 md:mt-0">
          <IconTitleDescCtaCard
            {...setupPaidEventCardStaticProps()}
            onCtaClick={goToCreatePaidEventPage}
          />
        </div>
        {/* Setup Paid Library content Card */}
        <div className="w-full h-full mt-12 rounded-12 md:mt-0">
          <IconTitleDescCtaCard
            {...setupPaidLibraryCardStaticProps()}
            onCtaClick={goToCreatePaidLibraryContentPage}
          />
        </div>
      </>
    );
  };

  const renderPaidMembershipCard = () => {
    return (
      <div className="w-full h-full rounded-12">
        <IconTitleDescCtaCard
          {...setupPaidMembershipCardStaticProps()}
          onCtaClick={goToCreatePaidMembershipPage}
        />
      </div>
    );
  };

  const renderCards = () => {
    return (
      <div className="gap-12 md:grid md:grid-cols-2 lg:gap-16">
        {renderPaidMembershipCard()}
        {renderContentCards()}
      </div>
    );
  };

  const getActiveTabData = () => {
    switch (activeTabId) {
      case EARNINGS_TAB:
        return (
          <EarningsTab
            chartData={chartData}
            earningsInfo={earningsData}
            isLoading={isLoading}
            activeCommunity={activeCommunity}
            renderEmptyStateCards={renderCards}
            renderContentCards={renderContentCards}
            renderPaidMembershipCard={renderPaidMembershipCard}
            hasPaidProducts={hasPaidProducts}
            isPaidCommunity={isPaidCommunity}
            hasPaidSubscriptions={hasPaidSubscriptions}
            membershipCounts={membershipCounts}
            actionBarProps={{
              monthFilterTooltip,
              handleCloseMonthFilterTooltip,
              onChangeMonthFilter,
              revenueTypes,
              revenueTypesSelected,
              setRevenueTypesSelected
            }}
          />
        );
      case TRANSACTIONS_TAB:
        return (
          <TransactionsTab
            payoutInfo={payoutInfo}
            activeCommunity={activeCommunity}
            isGtEqMd={isGtEqMd}
          />
        );
      case PAYOUT_TAB:
        // Only show new Payload tab for whitelisted communities if whitelist is enabled
        const isPayoutTabV2Enabled = config.payoutWhitelistEnabled;
        if (
          !isPayoutTabV2Enabled ||
          (isPayoutTabV2Enabled &&
            config.payoutWhitelistIDs?.includes(activeCommunity?._id))
        )
          return (
            <PayoutTabV2
              isGtEqMd={isGtEqMd}
              activeCommunity={activeCommunity}
              user={user}
            />
          );
        else
          return (
            <PayoutTab activeCommunity={activeCommunity} user={user} />
          );
      case FEES_TAB:
        return (
          <FeesTab
            activeCommunity={activeCommunity}
            localEditActiveCommunity={localEditActiveCommunity}
          />
        );
      default:
        null;
    }
  };
  // Mark page as viewed.
  useEffect(() => {
    const communityId = activeCommunity?._id;
    const { hasViewedMoneyPage } = activeCommunity || {};
    if (!communityId && !hasViewedMoneyPage) return;

    setMoneyPageViewedService(communityId, {
      hasViewedMoneyPage: true
    });

    // Save record in session.
    sessionStorageService.setItem(
      COMM_MONEY_PAGE_VIEW_MAP_SESSION_KEY,
      JSON.stringify({
        [communityId]: 1
      })
    );
  }, [activeCommunity]);

  return (
    <div className={`${className} p-32 px-16 md:px-32`}>
      <StatusTabs
        tabs={getAccessibleMoneyTabs(user)}
        onClick={onStatusTabClick}
        activeTabId={activeTabId}
        hasUnderline
      />
      <div className="max-w-[880px]">{getActiveTabData()}</div>
    </div>
  );
};

export default MoneyPageContent;
